/**
 * Enemy stepping. Each tick:
 *  1. Compute BFS distance map from the player tile (once, shared across enemies).
 *  2. For each enemy: if stunned, skip; otherwise dispatch on enemy.personality
 *     to chaser / interceptor / anticipator / wanderer and call stepCar.
 *
 * Enemy spawn positions are picked deterministically from the maze: of the
 * corridor tiles that are reachable from the player spawn under the current
 * boulder layout, the N with the largest Manhattan distance from the spawn,
 * tie-broken by row-major order. So same maze => same enemy spawns.
 */

import type { Direction, Enemy, EnemyPersonality, GameState, InputFrame } from './types.ts';
import { reachableTilesFrom, type Maze } from './maze.ts';
import { spawnCar, atTileCenter, stepCar } from './movement.ts';
import { toTile } from './fixed.ts';
import {
  bfsDistanceMap,
  pickAnticipatorDirection,
  pickEnemyDirection,
  pickInterceptorDirection,
  pickWandererDirection,
} from './ai.ts';
import { enemySpeedForStage } from './stage.ts';
import { personalityForEnemy } from './personality.ts';

// AIDEV-NOTE: enemy spawns MUST be reachable from the player spawn under the
// boulder layout. Boulder placement only guarantees flag-reachability, so a
// pocket of corridor can be cut off from spawn while still being a valid
// "corridor tile". An enemy that lands there has no BFS distance from the
// player, pickEnemyDirection returns null, and the enemy never moves.
// reachableTilesFrom(..., true) already excludes boulder tiles, so a separate
// boulder filter would be redundant here.
export function pickEnemySpawns(maze: Maze, count: number): { x: number; y: number }[] {
  const spawn = maze.spawn;
  const reachable = reachableTilesFrom(maze, spawn, true);
  const all: { x: number; y: number }[] = [];
  for (const key of reachable) {
    const [xs, ys] = key.split(',');
    const x = Number(xs);
    const y = Number(ys);
    if (x === spawn.x && y === spawn.y) continue;
    all.push({ x, y });
  }
  return all
    .map((t) => ({ t, d: Math.abs(t.x - spawn.x) + Math.abs(t.y - spawn.y) }))
    .sort((a, b) => b.d - a.d || a.t.y - b.t.y || a.t.x - b.t.x)
    .slice(0, count)
    .map(({ t }) => ({ x: t.x, y: t.y }));
}

export function createEnemies(maze: Maze, count: number, stage: number): Enemy[] {
  return pickEnemySpawns(maze, count).map((t, i) => ({
    id: i,
    car: spawnCar(t.x, t.y, null),
    stunUntil: 0,
    personality: personalityForEnemy(stage, i),
  }));
}

/** Interceptor lookahead — grows with stage so later enemies see further. */
function interceptorLookahead(stage: number): number {
  return Math.min(3 + Math.floor(stage / 2), 8);
}

/** Step all enemies. Pure: returns new state. */
export function stepEnemies(state: GameState): GameState {
  if (state.enemies.length === 0) return state;

  const playerTile = { x: toTile(state.player.x), y: toTile(state.player.y) };
  const distMap = bfsDistanceMap(state.maze, playerTile);

  const speed = enemySpeedForStage(state.stage, state.config);
  const enemies = state.enemies.map((e) =>
    stepOneEnemy(e, state, playerTile, distMap, speed),
  );
  return { ...state, enemies };
}

function stepOneEnemy(
  enemy: Enemy,
  state: GameState,
  playerTile: { x: number; y: number },
  distMap: Map<string, number>,
  speed: number,
): Enemy {
  // Stunned enemies don't move (or think).
  if (state.tick < enemy.stunUntil) return enemy;

  let chosenDir: Direction | null = enemy.car.dir;
  if (atTileCenter(enemy.car)) {
    const tx = toTile(enemy.car.x);
    const ty = toTile(enemy.car.y);
    chosenDir = pickDirectionFor(
      enemy.personality,
      state,
      enemy,
      { x: tx, y: ty },
      playerTile,
      distMap,
    );
  }

  const input: InputFrame = { dir: chosenDir, smoke: false };
  const car = stepCar(enemy.car, input, state.maze, speed);
  return { ...enemy, car };
}

function pickDirectionFor(
  personality: EnemyPersonality,
  state: GameState,
  enemy: Enemy,
  from: { x: number; y: number },
  playerTile: { x: number; y: number },
  playerDistMap: Map<string, number>,
): Direction | null {
  switch (personality) {
    case 'chaser':
      return pickEnemyDirection(state.maze, from, enemy.car.dir, playerDistMap);
    case 'interceptor':
      return pickInterceptorDirection(
        state.maze,
        from,
        enemy.car.dir,
        playerTile,
        state.player.dir,
        interceptorLookahead(state.stage),
        playerDistMap,
      );
    case 'anticipator':
      return pickAnticipatorDirection(
        state.maze,
        from,
        enemy.car.dir,
        state.flags,
        playerDistMap,
      );
    case 'wanderer':
      return pickWandererDirection(
        state.maze,
        from,
        enemy.car.dir,
        playerDistMap,
        state.tick,
        enemy.id,
        state.stage,
      );
  }
}
